import { motion } from 'motion/react';
import { Crown, Sparkles, Home, Shield, Lock, Diamond, ArrowRight, Globe, UserCheck, HeartHandshake } from 'lucide-react';

interface ForHighNetWorthPathwayProps {
  onNavigateHome: () => void;
  onNavigateToEliteConcierge: () => void;
  onNavigateToSpiritualWellness: () => void;
  onNavigateToHomeManagement: () => void;
}

export function ForHighNetWorthPathway({
  onNavigateHome,
  onNavigateToEliteConcierge,
  onNavigateToSpiritualWellness,
  onNavigateToHomeManagement
}: ForHighNetWorthPathwayProps) {
  return (
    <div className="min-h-screen bg-white">
      {/* Section 1: Hero Header */}
      <section className="relative pt-32 pb-24 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
          <div className="absolute bottom-20 left-20 w-96 h-96 rounded-full bg-[#C6A667] blur-3xl" />
        </div>

        <div className="relative max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <div className="inline-flex items-center gap-2 bg-[#C6A667]/20 px-4 py-2 rounded-full mb-6">
                <Diamond className="w-4 h-4 text-[#C6A667]" />
                <span className="text-[#C6A667] text-sm uppercase tracking-wider">Exclusive Services</span>
              </div>
              <h1 className="text-white mb-6 text-[48px]">Exclusive Concierge Services for Your Life in the U.S.</h1>
              <p className="text-white/90 text-lg leading-relaxed mb-8">
                Personalized, confidential, and high-touch support — designed for elite lifestyles.
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-8 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl"
              >
                Book Free 15-min Consultation
              </motion.button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl border-2 border-[#C6A667]/20">
                <img 
                  src="https://images.unsplash.com/photo-1759020623226-73ec7a068b11?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsaWZlc3R5bGUlMjByZXNpZGVuY2V8ZW58MXx8fHwxNzY1MTEzOTAyfDA&ixlib=rb-4.1.0&q=80&w=1080&utm_source=figma&utm_medium=referral"
                  alt="Luxury lifestyle residence"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Section 2: Tailored Life Support */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Tailored Life Support</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Comprehensive, discreet support for every aspect of your elite lifestyle
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {[
              {
                icon: Shield,
                title: 'Relocation services with privacy priority',
                description: 'White-glove relocation coordination with the utmost discretion and attention to detail'
              },
              {
                icon: Crown,
                title: 'Family education and lifestyle solutions',
                description: 'Exclusive school placement, cultural integration, and comprehensive family support'
              },
              {
                icon: Globe,
                title: 'Investment-related coordination (non-advisory)',
                description: 'Coordinate with your advisors, attorneys, and financial professionals seamlessly'
              },
              {
                icon: HeartHandshake,
                title: 'Personal concierge for sensitive requests',
                description: 'High-touch support for personal matters requiring confidentiality and excellence'
              }
            ].map((benefit, index) => {
              const IconComponent = benefit.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#C6A667]/30"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-[#C6A667] to-[#D4B578] rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-2">{benefit.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{benefit.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 3: Recommended Service Bundle */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Exclusive Service Portfolio</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Premium services designed for discerning clients
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: 'Elite Concierge Services',
                description: 'White-glove lifestyle management with priority access and complete discretion.',
                icon: Crown,
                onClick: onNavigateToEliteConcierge
              },
              {
                title: 'Spiritual Wellness',
                description: 'Personalized energy readings, Feng Shui, and Ba Zi guidance for holistic wellbeing.',
                icon: Sparkles,
                onClick: onNavigateToSpiritualWellness
              },
              {
                title: 'Home Management',
                description: 'Comprehensive property care and household coordination for your residence.',
                icon: Home,
                onClick: onNavigateToHomeManagement
              }
            ].map((service, index) => {
              const IconComponent = service.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  whileHover={{ y: -5 }}
                  className="group bg-white rounded-xl p-8 border-2 border-[#C6A667]/20 hover:border-[#C6A667] transition-all duration-300 cursor-pointer shadow-lg hover:shadow-2xl"
                  onClick={service.onClick}
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/80 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                    <IconComponent className="w-8 h-8 text-[#C6A667]" />
                  </div>
                  <h3 className="text-[#0A1A33] mb-3">{service.title}</h3>
                  <p className="text-gray-600 mb-6 leading-relaxed">{service.description}</p>
                  <button 
                    className="group/btn inline-flex items-center text-[#C6A667] hover:text-[#D4B578] transition-colors"
                    onClick={(e) => {
                      e.stopPropagation();
                      service.onClick();
                    }}
                  >
                    <span>Explore Service</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 4: Lifestyle Management Scenarios */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">Premium Lifestyle Scenarios</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Examples of how we support our elite clientele
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Home,
                title: 'Setting up a U.S. base residence',
                description: 'Complete property acquisition support, interior coordination, staffing, and ongoing home management for your primary or secondary residence.'
              },
              {
                icon: Crown,
                title: 'Children\'s education + home support package',
                description: 'Elite school placement, family cultural integration, and comprehensive home management while you focus on your priorities.'
              },
              {
                icon: Lock,
                title: 'Discreet handling of personal arrangements',
                description: 'Confidential coordination of sensitive matters, VIP experiences, and complex personal logistics with absolute discretion.'
              }
            ].map((scenario, index) => {
              const IconComponent = scenario.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.15 }}
                  className="relative bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg border border-gray-100"
                >
                  <div className="absolute top-0 right-0 w-32 h-32 bg-[#C6A667]/5 rounded-full -mr-16 -mt-16 blur-2xl" />
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#C6A667]/20 to-[#C6A667]/10 rounded-full flex items-center justify-center mb-6">
                      <IconComponent className="w-7 h-7 text-[#C6A667]" />
                    </div>
                    <h3 className="text-[#0A1A33] mb-3">{scenario.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{scenario.description}</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Section 5: How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-[#0A1A33] mb-4 text-[36px]">How It Works</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              A refined process designed for exceptional service
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                number: '01',
                title: 'Private Consultation',
                description: 'Discreet discussion of your needs and preferences'
              },
              {
                number: '02',
                title: 'Bespoke Proposal',
                description: 'Custom-designed service plan with transparent pricing'
              },
              {
                number: '03',
                title: 'White-Glove Execution',
                description: 'Seamless coordination with absolute attention to detail'
              },
              {
                number: '04',
                title: 'Lifestyle Excellence',
                description: 'Enjoy your life while we handle everything'
              }
            ].map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
                className="relative"
              >
                <div className="text-[#C6A667]/20 mb-4 text-[64px] leading-none">{step.number}</div>
                <h3 className="text-[#0A1A33] mb-2">{step.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{step.description}</p>
                {index < 3 && (
                  <div className="hidden md:block absolute top-12 -right-4 w-8 h-px bg-[#C6A667]/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 6: Confidentiality Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative bg-gradient-to-br from-[#0A1A33] to-[#0A1A33]/90 rounded-2xl p-12 overflow-hidden"
          >
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-[#C6A667] blur-3xl" />
            </div>
            <div className="relative text-center">
              <Lock className="w-16 h-16 text-[#C6A667] mx-auto mb-6" />
              <blockquote className="text-white text-2xl leading-relaxed mb-6 italic">
                &quot;We value your privacy at the highest standard — discretion is fundamental.&quot;
              </blockquote>
              <div className="flex items-center justify-center gap-8 mt-8">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-[#C6A667]" />
                  <span className="text-white/80 text-sm">Confidential</span>
                </div>
                <div className="flex items-center gap-2">
                  <UserCheck className="w-5 h-5 text-[#C6A667]" />
                  <span className="text-white/80 text-sm">Vetted Team</span>
                </div>
                <div className="flex items-center gap-2">
                  <Lock className="w-5 h-5 text-[#C6A667]" />
                  <span className="text-white/80 text-sm">Secure Process</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section 7: Final CTA */}
      <section className="py-24 bg-gradient-to-br from-[#0A1A33] via-[#0D2347] to-[#0A1A33]">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <Diamond className="w-12 h-12 text-[#C6A667] mx-auto mb-6" />
            <h2 className="text-white mb-8 text-[40px]">
              Let us take care of everything that matters.
            </h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
              className="group px-10 py-4 bg-[#C6A667] hover:bg-[#D4B578] text-white rounded-full transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center gap-3"
            >
              <span>Book Free 15-min Consultation</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
